import PageSEO from '@/components/PageSEO';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Phone, Building2, Shield, Zap } from 'lucide-react';
import ProudlyServingSection from '@/components/ProudlyServingSection';

export const StorefrontPage = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Commercial Storefront Glass Installation",
    "name": "Storefront & Curtain Wall Glass",
    "description": "Commercial storefront glass, curtain walls, and impact-resistant glazing systems with energy-efficient options and code-compliant installation.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Comal Glass Company",
      "telephone": "+1-830-359-9925"
    },
    "areaServed": { "@type": "State", "name": "Texas" }
  };

  const features = [
    'Custom storefront systems',
    'Curtain wall installations',
    'Energy-efficient glazing',
    'Impact-resistant options',
    'Various frame finishes',
    'Automatic door integration',
    'Weather seal systems',
    'Code-compliant installations',
  ];

  const benefits = [
    {
      icon: Building2,
      title: 'Professional Image',
      description: 'Make a lasting first impression with sleek, modern glass storefronts.',
    },
    {
      icon: Shield,
      title: 'Security & Safety',
      description: 'Impact-resistant glass and robust framing systems for peace of mind.',
    },
    {
      icon: Zap,
      title: 'Energy Efficiency',
      description: 'Advanced glazing reduces heating and cooling costs year-round.',
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Storefront & Curtain Wall Glass" description="Commercial storefront and curtain wall glass systems. Energy-efficient, impact-resistant, and code-compliant installations for retail, restaurants, and offices in the Texas Hill Country." path="/storefront-curtain-walls" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/services/Impact-Storefront-and-IG-Portfolio.jpg"
            alt="Commercial storefront glass"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        <div className="container-custom relative z-10 text-white">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
              Commercial Glass Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Storefront & <span className="text-gradient-cyan">Curtain Walls</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              Commercial glass installations that make a lasting impression. From retail storefronts to office buildings, we deliver professional results that elevate your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Commercial Advantages</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Elevate Your <span className="text-gradient-cyan">Business</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Professional glass installations that combine aesthetics with functionality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Our Services</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Comprehensive <span className="text-gradient-cyan">Commercial Solutions</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold mb-4">Storefront Systems</h3>
                <p className="text-muted-foreground mb-4">
                  Complete storefront installations including aluminum framing, glass panels, and entry doors. Perfect for retail, restaurants, and service businesses.
                </p>
                <ul className="space-y-2">
                  {[
                    'Custom aluminum frame systems',
                    'Single and double-pane glazing',
                    'Various door configurations',
                    'Multiple finish options',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold mb-4">Curtain Wall Systems</h3>
                <p className="text-muted-foreground mb-4">
                  Floor-to-ceiling glass wall systems for modern commercial buildings. Engineered for performance, aesthetics, and energy efficiency.
                </p>
                <ul className="space-y-2">
                  {[
                    'Multi-story installations',
                    'High-performance glazing',
                    'Structural integrity guaranteed',
                    'Energy-efficient designs',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Features & Specifications</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Built to <span className="text-gradient-cyan">Last</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-none bg-card">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <p className="text-sm">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Industries We Serve</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Trusted by <span className="text-gradient-cyan">Businesses</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              'Retail Stores',
              'Restaurants & Cafes',
              'Office Buildings',
              'Medical Facilities',
              'Hotels & Hospitality',
              'Financial Institutions',
              'Shopping Centers',
              'Corporate Campuses',
            ].map((industry, index) => (
              <Card key={index} className="border-none shadow-md text-center">
                <CardContent className="pt-6">
                  <p className="font-medium">{industry}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Our Process</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Professional <span className="text-gradient-cyan">Installation</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: '01', title: 'Site Assessment', description: 'Detailed evaluation of your commercial space' },
              { step: '02', title: 'Design & Engineering', description: 'Custom plans meeting all code requirements' },
              { step: '03', title: 'Fabrication', description: 'Precision manufacturing of all components' },
              { step: '04', title: 'Installation', description: 'Professional installation by certified crews' },
              { step: '05', title: 'Final Inspection', description: 'Quality check and customer walkthrough' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-gradient-cyan mb-3">{item.step}</div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Recent <span className="text-gradient-cyan">Commercial Projects</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              '/images/portfolio/Image-1.jpeg',
              '/images/portfolio/Image.jpeg',
              '/images/services/new-storefront-glass-construction.jpg',
            ].map((image, index) => (
              <div key={index} className="aspect-square overflow-hidden rounded-lg shadow-lg">
                <img src={image} alt={`Commercial project ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/portfolio">View All Projects</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Proudly Serving Section */}
      <ProudlyServingSection serviceSlug="storefront-curtain-walls" />

      {/* CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Upgrade Your <span className="text-gradient-cyan">Commercial Space?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contact us for a professional consultation and competitive quote for your storefront or curtain wall project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-border" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  (830) 359-9925
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StorefrontPage;
